import { businessProposalsService } from '@/services/proposals/business';
import type { ProposalAnalysisRecord } from '@/services/proposals/types';
import { useQuery } from '@tanstack/react-query';
import type {
  ProposalAnalysisAttachment,
  ProposalAnalysisInteraction,
} from '../types';

function extractFileName(keyPath: string, fallbackId: string): string {
  if (!keyPath) return fallbackId;
  const parts = keyPath.split('/');
  return parts[parts.length - 1] || fallbackId;
}

function extractDocumentType(name: string): string {
  const ext = name.split('.').pop();
  return ext ? ext.toUpperCase() : 'Arquivo';
}

function mapRecord(record: ProposalAnalysisRecord): ProposalAnalysisInteraction {
  const attachments: ProposalAnalysisAttachment[] = (
    record.attachments ?? []
  ).map(attachment => {
    const name = extractFileName(attachment.keyPath, attachment.id);
    return {
      id: attachment.id,
      name,
      documentType: extractDocumentType(name),
      keyPath: attachment.keyPath,
    };
  });

  return {
    id: record.id,
    createdAt: record.createdAt,
    action: record.action,
    status: record.status,
    description: record.description,
    rejectionReason: record.rejectionReason,
    attachments,
    userName: record.userName?.trim() || '-',
  };
}

export function useProposalAnalysisInteractions(proposalId: string) {
  return useQuery({
    queryKey: ['proposal-analysis-records', proposalId],
    queryFn: async () => {
      const response =
        await businessProposalsService.getAnalysisRecords(proposalId);

      if (!response.success || !response.data) {
        throw new Error(
          response.error || 'Erro ao carregar interações da análise'
        );
      }

      return response.data.map(mapRecord);
    },
    enabled: Boolean(proposalId?.trim()),
    staleTime: 0,
  });
}
